import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../../../../components/layout/Header';
import { PageHeader } from '../../../../components/ui';
import { SkillsManager } from '../../../../components/skills/SkillsManager';
import { fetchRepoCatalog } from '../../../../lib/api/forgeConfig';
import {
  deleteRepoSkill,
  listRepoSkills,
  previewProjectSkill,
  previewRepoSkill,
  toggleRepoSkill,
  uploadRepoSkill,
  type Skill,
  type SkillListItem,
  type SkillPreview,
} from '../../../../lib/api/skills';

/**
 * /repos/:org/:repo/skills — repo-scope skill management with the
 * project's inherited skills surfaced read-only.
 *
 * "Project" badged rows can't be deleted or toggled from here; the
 * empty-state and banner direct the user to the project's own Skills
 * tab when they want to edit the inherited defaults.
 *
 * UX-04: retitled "Skill overrides · org/repo" to match what this page
 * actually is (the repo-scope override surface, not a duplicate of the
 * project Skills tab). The back link points at the owning project's
 * Skills section when the repo belongs to exactly one project — the
 * common case — and falls back to the /repos landing page otherwise.
 */
export const RepoSkillsPage: React.FC = () => {
  const params = useParams<{ org: string; repo: string }>();
  const org = params.org ?? '';
  const repo = params.repo ?? '';
  const fullName = `${org}/${repo}`;

  const catalogQ = useQuery({
    queryKey: ['repos', 'catalog'],
    queryFn: fetchRepoCatalog,
  });
  const catalogEntry = catalogQ.data?.find((r) => r.fullName === fullName);
  const owningProjectId =
    catalogEntry && catalogEntry.projectIds.length === 1 ? catalogEntry.projectIds[0] : null;

  const backTo = owningProjectId ? `/projects/${owningProjectId}?tab=agents&section=skills` : '/repos';
  const backLabel = owningProjectId ? '← Back to project skills' : '← Back to repos';

  return (
    <div data-testid="repo-skills-page" className="min-h-screen bg-slate-50">
      <Header title={`Skill overrides · ${fullName}`} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <Link to={backTo} className="text-xs text-primary-500 hover:text-primary-600 hover:underline">
          {backLabel}
        </Link>
        <PageHeader
          title={`Skill overrides · ${fullName}`}
          description="Skills uploaded here apply to this repo only and override any project default with the same slug. Project skills are shown read-only."
        />

        <SkillsManager
          scopeKey={['repo', org, repo]}
          list={() => listRepoSkills(org, repo)}
          upload={(file: File, opts: { name?: string; description?: string }): Promise<Skill> =>
            uploadRepoSkill(org, repo, { file, name: opts.name, description: opts.description })
          }
          preview={(item: SkillListItem): Promise<SkillPreview> => {
            // Project-scope rows are inherited from the parent project,
            // so we have to call the project's own preview endpoint —
            // the repo route only knows about its own rows. Recover the
            // owning project id from parentPk ("PROJECT#<id>").
            if (item.scope === 'project') {
              const projectId = item.parentPk.replace(/^PROJECT#/, '');
              return previewProjectSkill(projectId, item.slug);
            }
            return previewRepoSkill(org, repo, item.slug);
          }}
          toggle={(item: SkillListItem, active: boolean): Promise<Skill> =>
            toggleRepoSkill(org, repo, item.slug, active)
          }
          remove={(item: SkillListItem): Promise<void> =>
            deleteRepoSkill(org, repo, item.slug)
          }
          rowReadOnly={(item) => item.scope === 'project'}
          banner={
            <div className="text-xs text-slate-600 bg-info-50 border border-info-200 rounded-md p-3">
              Skills inherited from a project are listed here for visibility. To edit them,
              open the owning project's Skills tab.
            </div>
          }
        />
      </main>
    </div>
  );
};

export default RepoSkillsPage;
